import { useEffect, useState, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { Lock, Unlock, UserPlus, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AdminHeader, { useAdminGuard } from "../components/admin/AdminHeader";
import ManualBookingDialog from "../components/admin/ManualBookingDialog";
import { Calendar } from "../components/ui/calendar";

const BLOCKS = [
  { label: "09:00 – 12:00", nice: "9am – 12pm", slots: ["09:00", "10:00", "11:00"], slot: "09:00" },
  { label: "12:00 – 15:00", nice: "12pm – 3pm", slots: ["12:00", "13:00", "14:00"], slot: "12:00" },
  { label: "15:00 – 18:00", nice: "3pm – 6pm", slots: ["15:00", "16:00", "17:00"], slot: "15:00" },
];
const MAX_PER_BLOCK = 2;

export default function AdminSchedule() {
  const onError = useAdminGuard();
  const [day, setDay] = useState(new Date());
  const [blocks, setBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogBlock, setDialogBlock] = useState(null);
  const dateStr = format(day, "yyyy-MM-dd");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bl, bk] = await Promise.all([api.get("/admin/blocks"), api.get("/admin/bookings")]);
      setBlocks(bl.data);
      setBookings(bk.data);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const dayBlocks = blocks.filter((b) => b.date === dateStr);
  const wholeDay = dayBlocks.find((b) => b.block === null);
  const dayBookings = bookings.filter((b) => b.booking_date === dateStr && b.status !== "cancelled");
  const blockedDays = useMemo(() => blocks.filter((b) => b.block === null).map((b) => new Date(`${b.date}T12:00:00`)), [blocks]);
  const bookedDays = useMemo(() => [...new Set(bookings.filter((b) => b.status !== "cancelled").map((b) => b.booking_date))].map((d) => new Date(`${d}T12:00:00`)), [bookings]);

  const addBlock = async (block) => {
    try {
      await api.post("/admin/blocks", { date: dateStr, block });
      toast.success(block ? `${block} blocked` : "Whole day blocked");
      load();
    } catch (err) { onError(err); }
  };
  const removeBlock = async (id) => {
    try {
      await api.delete(`/admin/blocks/${id}`);
      toast.success("Reopened");
      load();
    } catch (err) { onError(err); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-schedule">
      <AdminHeader onRefresh={load} loading={loading} />
      <main className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[auto_1fr] gap-10">
        <aside>
          <h2 className="font-display font-bold text-2xl tracking-tight mb-1">Schedule</h2>
          <p className="text-sm text-zinc-500 mb-5">Pick a day to block slots or book someone in.</p>
          <div className="rounded-sm border border-white/5 bg-[#0a0a0a] inline-block" data-testid="schedule-calendar">
            <Calendar
              mode="single"
              selected={day}
              onSelect={(d) => d && setDay(d)}
              modifiers={{ blocked: blockedDays, booked: bookedDays }}
              classNames={{ day_selected: "!bg-[#D946EF] !text-white hover:!bg-[#D946EF]", day_today: "ring-1 ring-white/30" }}
              modifiersClassNames={{ blocked: "!bg-red-500/20 !text-red-300 line-through", booked: "font-bold !text-[#D946EF]" }}
            />
          </div>
          <div className="mt-4 flex gap-4 text-xs text-zinc-500">
            <span><span className="text-[#D946EF] font-bold">Pink</span> = has bookings</span>
            <span><span className="text-red-300 line-through">Red</span> = day blocked</span>
          </div>
        </aside>

        <section>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Selected day</div>
              <div className="font-display font-bold text-3xl" data-testid="schedule-selected-date">{format(day, "EEEE d MMMM yyyy")}</div>
            </div>
            {wholeDay ? (
              <button onClick={() => removeBlock(wholeDay.id)} data-testid="unblock-day-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-green-500/40 text-green-300 text-sm hover:bg-green-500/10 transition-colors">
                <Unlock className="w-4 h-4" /> Reopen whole day
              </button>
            ) : (
              <button onClick={() => addBlock(null)} data-testid="block-day-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-500/40 text-red-300 text-sm hover:bg-red-500/10 transition-colors">
                <CalendarOff className="w-4 h-4" /> Block whole day
              </button>
            )}
          </div>

          {wholeDay && (
            <div className="mb-5 px-4 py-3 rounded-sm border border-red-500/30 bg-red-500/5 text-sm text-red-300" data-testid="day-blocked-banner">
              This whole day is blocked. Customers can't book any slot.
            </div>
          )}

          <div className="grid gap-4">
            {BLOCKS.map((blk) => (
              <BlockCard
                key={blk.label}
                blk={blk}
                blockDoc={dayBlocks.find((b) => b.block === blk.label)}
                dayBlocked={!!wholeDay}
                items={dayBookings.filter((b) => blk.slots.includes(b.time_slot))}
                onBlock={() => addBlock(blk.label)}
                onUnblock={(id) => removeBlock(id)}
                onBook={() => setDialogBlock(blk)}
              />
            ))}
          </div>
        </section>
      </main>
      <ManualBookingDialog
        open={!!dialogBlock}
        onOpenChange={(o) => !o && setDialogBlock(null)}
        date={dateStr}
        block={dialogBlock}
        onError={onError}
        onSaved={load}
      />
    </div>
  );
}

function BlockCard({ blk, blockDoc, dayBlocked, items, onBlock, onUnblock, onBook }) {
  const id = blk.slot.replace(":", "");
  const closed = dayBlocked || !!blockDoc;
  const full = items.length >= MAX_PER_BLOCK;
  const status = closed ? "Blocked" : full ? "Full" : `${MAX_PER_BLOCK - items.length} of ${MAX_PER_BLOCK} open`;
  return (
    <div className={`rounded-sm border p-5 transition-colors ${closed ? "border-red-500/30 bg-red-500/[0.04]" : "border-white/5 bg-[#0a0a0a]"}`} data-testid={`block-card-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-bold text-xl">{blk.nice}</div>
          <div className={`text-xs mt-0.5 ${closed ? "text-red-300" : full ? "text-yellow-300" : "text-green-300"}`} data-testid={`block-status-${id}`}>{status}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onBook} disabled={full} data-testid={`book-in-btn-${id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D946EF] text-white text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition-colors">
            <UserPlus className="w-4 h-4" /> Book customer in
          </button>
          {!dayBlocked && (blockDoc ? (
            <button onClick={() => onUnblock(blockDoc.id)} data-testid={`unblock-slot-btn-${id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-green-500/40 text-green-300 text-sm hover:bg-green-500/10 transition-colors">
              <Unlock className="w-4 h-4" /> Reopen slot
            </button>
          ) : (
            <button onClick={onBlock} data-testid={`block-slot-btn-${id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-zinc-300 text-sm hover:border-red-500/40 hover:text-red-300 transition-colors">
              <Lock className="w-4 h-4" /> Block slot
            </button>
          ))}
        </div>
      </div>
      {items.length > 0 && (
        <ul className="mt-4 divide-y divide-white/5 border-t border-white/5">
          {items.map((b) => (
            <li key={b.id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`block-booking-${b.id}`}>
              <div>
                <span className="text-white font-medium">{b.name}</span>
                <span className="text-zinc-500"> · {b.vehicle}{b.reg ? ` · ${b.reg}` : ""}</span>
              </div>
              <div className="text-xs text-zinc-400">
                {b.package || b.service}{b.source === "admin" ? <span className="ml-2 text-[#D946EF]">added by you</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
